import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSticker } from '../context/StickerContext';
import { Header } from '../components/layout/Header';
import { StepHeader } from '../components/ui/StepHeader';
import { Card } from '../components/ui/Card';
import { PriceSummaryRow } from '../components/ui/PriceSummaryRow';
import { SecondaryButtonOutline, PrimaryButtonGreen, PrimaryButtonPurple } from '../components/ui/Buttons';
import { SegmentedApprovalToggle } from '../components/ui/SegmentedApprovalToggle';
import { CheckboxRow } from '../components/ui/CheckboxRow';
import { CalloutWarning } from '../components/ui/CalloutWarning';
import { Laptop } from 'lucide-react';
export function OrderSummaryPage() {
  const navigate = useNavigate();
  const {
    uploadedFile,
    quantity,
    size,
    material,
    lamination,
    cutline,
    totalPrice,
    pricePerSticker
  } = useSticker();
  const [approvalMode, setApprovalMode] = useState('I approve this design');
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [addNote, setAddNote] = useState(false);
  return <div className="min-h-screen bg-gray-50" data-id="element-317">
      <Header data-id="element-318" />

      <main className="max-w-7xl mx-auto px-4 py-8" data-id="element-319">
        <StepHeader backTo="/stickers" stepText="Step 2 of 2 · Order summary" data-id="element-320" />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-321">
          {/* LEFT CARD - YOUR DESIGN */}
          <Card title="Your Design" titleRight={<Link to="/stickers" className="text-sm font-medium text-violet-600 hover:text-violet-700" data-id="element-323">
                Edit
              </Link>} data-id="element-322">
            <div className="space-y-4 text-sm" data-id="element-324">
              <div className="flex justify-between py-2 border-b border-gray-50" data-id="element-325">
                <span className="text-gray-500" data-id="element-326">File</span>
                <span className="font-medium text-gray-900" data-id="element-327">
                  {uploadedFile?.name || 'No file'}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-50" data-id="element-328">
                <span className="text-gray-500" data-id="element-329">Quantity</span>
                <span className="font-medium text-gray-900" data-id="element-330">
                  {quantity} stickers
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-50" data-id="element-331">
                <span className="text-gray-500" data-id="element-332">Size</span>
                <span className="font-medium text-gray-900" data-id="element-333">{size}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-50" data-id="element-334">
                <span className="text-gray-500" data-id="element-335">Material</span>
                <span className="font-medium text-gray-900" data-id="element-336">{material}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-50" data-id="element-337">
                <span className="text-gray-500" data-id="element-338">Lamination</span>
                <span className="font-medium text-gray-900" data-id="element-339">{lamination}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-50" data-id="element-340">
                <span className="text-gray-500" data-id="element-341">Cutline</span>
                <span className="font-medium text-gray-900" data-id="element-342">{cutline}</span>
              </div>
            </div>

            <div className="mt-8 border-t border-gray-100" data-id="element-343">
              <PriceSummaryRow total={`$${totalPrice.toLocaleString('en-US', {
              minimumFractionDigits: 2
            })}`} perUnit={`$${pricePerSticker} per sticker`} data-id="element-344" />
            </div>
          </Card>

          {/* RIGHT CARD - SEE IT IN ACTION */}
          <Card title="See It in Action" data-id="element-345">
            {/* Preview Placeholder */}
            <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center mb-6 border border-gray-200" data-id="element-346">
              <Laptop size={48} className="text-gray-300" data-id="element-347" />
            </div>

            <SecondaryButtonOutline className="w-full mb-8" onClick={() => navigate('/stickers/editor')} data-id="element-348">
              Adjust Your Sticker
            </SecondaryButtonOutline>

            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-349">
              Do you approve this design?
            </h3>

            <div className="mb-6" data-id="element-350">
              <SegmentedApprovalToggle options={['I approve this design', 'I need a manual proof']} value={approvalMode} onChange={setApprovalMode} data-id="element-351" />
            </div>

            {approvalMode === 'I approve this design' ? <div className="space-y-6" data-id="element-352">
                <CheckboxRow checked={isConfirmed} onChange={setIsConfirmed} label="I have reviewed the sticker design and accept it" description="I confirm that I have checked the sticker preview and am satisfied with the design." data-id="element-353" />
                <PrimaryButtonGreen className="w-full" disabled={!isConfirmed} data-id="element-354">
                  Confirm my design(s)
                </PrimaryButtonGreen>
              </div> : <div className="space-y-6" data-id="element-355">
                <CalloutWarning data-id="element-356">
                  We'll double-check your design to make sure everything's
                  perfect. This may take a bit longer and we might reach out if
                  we need your confirmation.
                </CalloutWarning>

                <div className="space-y-3" data-id="element-357">
                  <CheckboxRow checked={addNote} onChange={setAddNote} label="Add a note" data-id="element-358" />
                  {addNote && <textarea className="w-full rounded-lg border border-gray-200 p-3 text-sm focus:ring-2 focus:ring-violet-500 outline-none min-h-[100px]" placeholder="Leave a note for our production team..." data-id="element-359" />}
                </div>

                <PrimaryButtonPurple className="w-full" data-id="element-360">
                  Request manual proof
                </PrimaryButtonPurple>
              </div>}
          </Card>
        </div>
      </main>
    </div>;
}